const MONTHS = ["sty", "lut", "mar", "kwi", "maj", "cze", "lip", "sie", "wrz", "paź", "lis", "gru"];

// Date -> "4 lis 2023" (Polish order; it used to print the US-style "Lis 4, 2023")
export function formatDate(
  date?: Date,
  withTime = false,
  withSeconds = false,
  withYear: boolean | "short" = true
): string {
  if (!date) return "";
  const dateIsString = typeof date === "string";
  if (dateIsString) date = new Date(date);

  const formatNumber = (num: number) => num.toString().padStart(2, "0");

  const day = date.getDate();
  const month = MONTHS[date.getMonth()];
  const year = date.getFullYear();

  const time = `${formatNumber(date.getHours())}:${formatNumber(
    date.getMinutes()
  )}${withSeconds ? `:${formatNumber(date.getSeconds())}` : ""}`;

  const yearPart = !withYear ? "" : withYear === "short" ? ` ${year.toString().slice(2)}` : ` ${year}`;
  return `${day} ${month}${yearPart}${withTime ? ` ${time}` : ""}`;
}

// "4 lis 2023" -> Date. formatDate's output is used as a select value (schedule days) and parsed back here, so the
// old "Lis 4, 2023" form is still accepted.
export function deFormatDate(str: string): Date | null {
  const parts = str.replace(",", "").split(" ").filter(Boolean);
  if (parts.length !== 3) {
    return null;
  }

  const monthFirst = isNaN(parseInt(parts[0], 10));
  const [dayText, monthText, yearText] = monthFirst ? [parts[1], parts[0], parts[2]] : parts;
  const month = MONTHS.indexOf(monthText.toLowerCase());
  const day = parseInt(dayText, 10);
  const year = parseInt(yearText, 10);

  if (month < 0 || isNaN(day) || isNaN(year)) {
    return null;
  }

  return new Date(year, month, day, 12, 0, 0);
}

// Date to time. Date -> "12:00"
export function formatTime(date: Date): string {
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");

  return `${hours}:${minutes}`;
}
// "12:00" -> Date
export function deFormatTime(str: string): Date | null {
  const parts = str.split(":");
  if (parts.length !== 2) {
    return null;
  }

  const hours = parseInt(parts[0], 10);
  const minutes = parseInt(parts[1], 10);

  if (isNaN(hours) || isNaN(minutes)) {
    return null;
  }

  return new Date(0, 0, 0, hours, minutes);
}

// substract time from time "12:00" - "10:00" and return minutes as number
// ex. "12:00" - "10:00" -> 120
export function subtractTime(timeStr1: string, timeStr2: string): number {
  const date1 = deFormatTime(timeStr1);
  const date2 = deFormatTime(timeStr2);

  if (!date1 || !date2) return 0;

  const diff = date1.getTime() - date2.getTime();
  return diff / 1000 / 60;
}

// substract time from time "12:00" - "10:00" and return minutes in specyfic format
// ex. "12:00" - "10:00" -> "2h 0m"
export function subtractTimeStr(timeStr1: string, timeStr2: string): string {
  const minutes = subtractTime(timeStr1, timeStr2);
  return minutesToTime(minutes);
}

export function addTime(timeStr: string, duration: number | string): string {
  // Parse the time string to a Date object
  if (!timeStr) return "00";
  const timeParts = timeStr.split(":");
  const date = new Date();
  date.setHours(+timeParts[0]);
  date.setMinutes(+timeParts[1]);

  // Add the duration in minutes
  date.setMinutes(date.getMinutes() + +duration);

  const newTimeStr = formatTime(date);

  return newTimeStr;
}

export function subtractDays(inputDate: Date, day: number): Date {
  if (!(inputDate instanceof Date)) throw "Input must be a Date object";

  let newDate = new Date(inputDate);

  newDate.setDate(inputDate.getDate() - day);

  return newDate;
}

export function addDays(date: Date, daysToAdd: number): Date {
  let resultDate = new Date(date);
  resultDate.setDate(resultDate.getDate() + daysToAdd);
  return resultDate;
}

// 2h 10m -> 130
export function timeToMinutes(timeStr?: string | null): number {
  if (!timeStr) return 0;
  let hours = 0;
  let minutes = 0;

  const hourMatch = timeStr.match(/(\d+)h/);
  if (hourMatch) {
    hours = parseInt(hourMatch[1], 10);
  }

  const minuteMatch = timeStr.match(/(\d+)m/);
  if (minuteMatch) {
    minutes = parseInt(minuteMatch[1], 10);
  }

  return hours * 60 + minutes;
}

// 130 -> 2h 10m
export function minutesToTime(minutesInput?: number | string): string {
  if (!minutesInput) return "";
  const isConveratbleToNumber = !isNaN(Number(minutesInput));
  if (isConveratbleToNumber) {
    const hours = Math.floor(Number(minutesInput) / 60);
    const minutes = Number(minutesInput) % 60;
    const hoursString = hours > 0 ? `${hours}h ` : "";
    const minutesString = minutes > 0 ? `${minutes}m` : "";

    return `${hoursString}${minutesString}`;
  }
  const isCorrectHourAndMinuteFormat =
    /^([0-1]?[0-9]|2[0-3])h([0-5]?[0-9])m$/.test(
      minutesInput.toString().trim()
    );
  return isCorrectHourAndMinuteFormat ? minutesInput.toString() : "";
}

export const daysToDate = (destinationDate: Date): number => {
  const now = new Date();

  const substractDate = destinationDate.getTime() - now.getTime();
  const substractDays = Math.ceil(substractDate / (1000 * 60 * 60 * 24));

  return substractDays;
};

// * 330 => "05:30"
export const toTimeFormat = (minutes: number) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, "0")}:${mins
    .toString()
    .padStart(2, "0")}`;
};

// "05:30" => 330
export const fromTimeFormat = (time: string) => {
  const [hours, mins] = time.split(":").map(Number);
  return hours * 60 + mins;
};

export const isParseableDate = (value: unknown): boolean =>
  value instanceof Date || !isNaN(Date.parse(value as string));

// Helper to get relative time string (e.g., '2 minuty temu')
export function getRelativeTime(date: Date | string): string {
  const now = new Date();
  const d = typeof date === "string" ? new Date(date) : date;
  const diff = Math.floor((now.getTime() - d.getTime()) / 1000);

  if (diff < 60) {
    if (diff === 1) return "1 sekunda temu";
    if (diff >= 2 && diff <= 4) return `${diff} sekundy temu`;
    return `${diff} sekund temu`;
  }

  const minutes = Math.floor(diff / 60);
  if (diff < 3600) {
    if (minutes === 1) return "1 minuta temu";
    if (minutes >= 2 && minutes <= 4) return `${minutes} minuty temu`;
    return `${minutes} minut temu`;
  }

  const hours = Math.floor(diff / 3600);
  if (diff < 86400) {
    if (hours === 1) return "1 godzina temu";
    if (hours >= 2 && hours <= 4) return `${hours} godziny temu`;
    return `${hours} godzin temu`;
  }

  const days = Math.floor(diff / 86400);
  if (days === 1) return "1 dzień temu";
  return `${days} dni temu`;
}
